import { useEffect, useMemo, useState } from "react";
import { applyTheme, getStoredTheme, setStoredTheme } from "../../utils/theme";
import { DISTRICT_EDO_ROLES, normalizeRole } from "../../constants/roles";

const keyForRole = (role) => `settings:${role || "unknown"}`;

const defaultsForRole = (role) => {
  switch (normalizeRole(role)) {
    case "admin":
      return { inAppNotifications: true, emailNotifications: true, compactTables: false };
    case "reviewer":
      return { inAppNotifications: true, emailNotifications: true, defaultQueue: "pending" };
    case "Environmental District Officer":
      return { inAppNotifications: true, emailNotifications: true, remindOverdue: true };
    case "viewer":
      return { compactTables: false, defaultStatusFilter: "all" };
    default:
      return { inAppNotifications: true };
  }
};

export default function PreferencesCard({ role, variant = "card", title = "Preferences" }) {
  const normalizedRole = useMemo(() => normalizeRole(role), [role]);
  const storageKey = useMemo(() => keyForRole(normalizedRole), [normalizedRole]);

  const [prefs, setPrefs] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : defaultsForRole(normalizedRole);
    } catch {
      return defaultsForRole(normalizedRole);
    }
  });

  const [theme, setTheme] = useState(() => getStoredTheme());

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const save = (next) => {
    setPrefs(next);
    localStorage.setItem(storageKey, JSON.stringify(next));
  };

  const toggle = (name) => (e) => save({ ...prefs, [name]: e.target.checked });

  const wrapperClass =
    variant === "plain" ? "w-full" : "bg-white dark:bg-gray-800 p-6 rounded-xl shadow w-full";

  return (
    <div className={wrapperClass}>
      <h3 className="text-lg font-semibold text-green-700 dark:text-green-200 mb-4">{title}</h3>

      <div className="space-y-4 text-sm">
        <div className="flex items-center justify-between gap-4">
          <span>
            <span className="font-semibold text-gray-800 dark:text-gray-100">Theme</span>
            <span className="block text-xs text-gray-500 dark:text-gray-400">Choose light or dark mode.</span>
          </span>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="theme"
                value="light"
                checked={theme === "light"}
                onChange={() => {
                  setTheme("light");
                  setStoredTheme("light");
                }}
              />
              <span className="text-xs text-gray-700 dark:text-gray-200">Light</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="theme"
                value="dark"
                checked={theme === "dark"}
                onChange={() => {
                  setTheme("dark");
                  setStoredTheme("dark");
                }}
              />
              <span className="text-xs text-gray-700 dark:text-gray-200">Dark</span>
            </label>
          </div>
        </div>

        {"inAppNotifications" in prefs && (
          <label className="flex items-center justify-between gap-4">
            <span>
              <span className="font-semibold text-gray-800 dark:text-gray-100">In-app notifications</span>
              <span className="block text-xs text-gray-500 dark:text-gray-400">
                Show notifications inside the portal.
              </span>
            </span>
            <input type="checkbox" checked={!!prefs.inAppNotifications} onChange={toggle("inAppNotifications")} />
          </label>
        )}

        {"emailNotifications" in prefs && (
          <label className="flex items-center justify-between gap-4">
            <span>
              <span className="font-semibold text-gray-800 dark:text-gray-100">Email notifications</span>
              <span className="block text-xs text-gray-500 dark:text-gray-400">
                Receive status updates by email (if enabled).
              </span>
            </span>
            <input type="checkbox" checked={!!prefs.emailNotifications} onChange={toggle("emailNotifications")} />
          </label>
        )}

        {role === "reviewer" && (
          <div className="flex items-center justify-between gap-4">
            <span>
              <span className="font-semibold text-gray-800 dark:text-gray-100">Default queue</span>
              <span className="block text-xs text-gray-500 dark:text-gray-400">
                Where you land when opening the reviewer section.
              </span>
            </span>
            <select
              className="border rounded px-2 py-1 bg-white dark:bg-gray-900 dark:border-gray-700"
              value={prefs.defaultQueue || "pending"}
              onChange={(e) => save({ ...prefs, defaultQueue: e.target.value })}
            >
              <option value="pending">Pending reviews</option>
              <option value="reviewed">Reviewed submissions</option>
            </select>
          </div>
        )}

        {DISTRICT_EDO_ROLES.includes(role) && (
          <label className="flex items-center justify-between gap-4">
            <span>
              <span className="font-semibold text-gray-800 dark:text-gray-100">Overdue reminders</span>
              <span className="block text-xs text-gray-500 dark:text-gray-400">
                Highlight overdue items and show reminders.
              </span>
            </span>
            <input type="checkbox" checked={!!prefs.remindOverdue} onChange={toggle("remindOverdue")} />
          </label>
        )}

        {role === "viewer" && (
          <div className="flex items-center justify-between gap-4">
            <span>
              <span className="font-semibold text-gray-800 dark:text-gray-100">Default status filter</span>
              <span className="block text-xs text-gray-500 dark:text-gray-400">
                Preselect a status on the document tracker.
              </span>
            </span>
            <select
              className="border rounded px-2 py-1 bg-white dark:bg-gray-900 dark:border-gray-700"
              value={prefs.defaultStatusFilter || "all"}
              onChange={(e) => save({ ...prefs, defaultStatusFilter: e.target.value })}
            >
              <option value="all">All</option>
              <option value="Submitted">Submitted</option>
              <option value="Pending">Pending</option>
              <option value="Overdue">Overdue</option>
              <option value="Approved">Approved</option>
              <option value="Returned">Returned</option>
              <option value="Rejected">Rejected</option>
            </select>
          </div>
        )}

        {"compactTables" in prefs && (
          <label className="flex items-center justify-between gap-4">
            <span>
              <span className="font-semibold text-gray-800 dark:text-gray-100">Compact tables</span>
              <span className="block text-xs text-gray-500 dark:text-gray-400">Use tighter spacing in tables.</span>
            </span>
            <input type="checkbox" checked={!!prefs.compactTables} onChange={toggle("compactTables")} />
          </label>
        )}

        <div className="pt-2 flex justify-end">
          <button
            className="text-xs px-3 py-1 rounded border hover:bg-gray-50 dark:hover:bg-gray-700/40 dark:border-gray-700"
            onClick={() => save(defaultsForRole(normalizedRole))}
          >
            Reset to defaults
          </button>
        </div>
      </div>
    </div>
  );
}
